// Open overlays (side panels + dialogs) as a stack, plus the coral registry.
//
// Coral discipline (business rule 2 / ERP §0): exactly ONE coral element per
// screen. While a side panel is open it owns the coral (board part 1 note:
// «لوحة جانبية ٤٢٠ … تملك المرجاني وهي مفتوحة»), so a <PrimaryAction> renders
// coral only when it sits at the TOP overlay depth (page = depth 0). The dev
// check warns when two coral actions are live at the same depth.

import { createContext, useContext, useSyncExternalStore } from 'react';

export type OverlayKind = 'panel' | 'dialog';

type Listener = () => void;
const listeners = new Set<Listener>();
let stack: Array<{ id: string; kind: OverlayKind }> = [];

function emit() {
  listeners.forEach((l) => l());
}

export const overlayStore = {
  push(id: string, kind: OverlayKind): number {
    stack = [...stack.filter((o) => o.id !== id), { id, kind }];
    emit();
    return stack.length;
  },
  remove(id: string): void {
    const next = stack.filter((o) => o.id !== id);
    if (next.length === stack.length) return;
    stack = next;
    emit();
  },
  depth: (): number => stack.length,
  depthOf: (id: string): number => stack.findIndex((o) => o.id === id) + 1,
  panelOpen: (): boolean => stack.some((o) => o.kind === 'panel'),
  subscribe(l: Listener): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  /** Test hook. */
  reset(): void {
    stack = [];
    emit();
  },
};

/**
 * The live depth of overlay `id` (1-based). Before its first push (the first
 * render) it is the depth it is about to take; afterwards it follows the
 * stack, so an overlay opened while another was still closing drops to the
 * top once that one leaves (and its <PrimaryAction> turns coral).
 */
export function useOverlayDepthOf(id: string): number {
  return useSyncExternalStore(overlayStore.subscribe, () => overlayStore.depthOf(id) || overlayStore.depth() + 1);
}

/** Depth of the overlay a component renders inside (0 = the page). */
export const OverlayDepthContext = createContext(0);
export function useOverlayDepth(): number {
  return useContext(OverlayDepthContext);
}

// --------------------------------------------------------------- coral --

const coral = new Map<string, number>(); // primary id → depth
let warned = false;

export const coralStore = {
  register(id: string, depth: number): void {
    coral.set(id, depth);
    const same = [...coral.values()].filter((d) => d === depth).length;
    if (same > 1 && import.meta.env.DEV && !warned) {
      warned = true;
      // eslint-disable-next-line no-console
      console.warn('[ehjizli] more than one coral <PrimaryAction> is live on this screen — exactly one decisive action per screen (rule 2).');
    }
  },
  unregister(id: string): void {
    coral.delete(id);
    if (coral.size === 0) warned = false;
  },
  /** Coral actions currently rendered as coral. */
  count: (): number => coral.size,
  reset(): void {
    coral.clear();
    warned = false;
  },
};
